import { Badge, Box, Button, Flex, Icon, IconButton, Input, InputGroup } from '@chakra-ui/react'
import { useTranslation } from '@infraloop/i18n'
import { LuCheck, LuPencil, LuRefreshCw, LuSearch, LuX } from 'react-icons/lu'

const UserListToolbar = ({
  editMode,
  editCount,
  search,
  isSaving,
  isFetching,
  onEnterEdit,
  onSave,
  onCancel,
  onRefetch,
  onSearchChange,
}: {
  /** Whether the toolbar is in HubSpot-ID edit mode. */
  editMode: boolean
  /** Number of rows with unsaved HubSpot-ID changes. */
  editCount: number
  /** Current search input text. */
  search: string
  /** Whether a save is in progress. */
  isSaving: boolean
  /** The list is (re)fetching — drives the refetch button's loading state. */
  isFetching: boolean
  /** Enter HubSpot-ID edit mode. */
  onEnterEdit: () => void
  /** Persist the pending HubSpot-ID changes. */
  onSave: () => void
  /** Discard pending changes and leave edit mode. */
  onCancel: () => void
  /** Refetch the current list. */
  onRefetch: () => void
  /** Update the search input text. */
  onSearchChange: (value: string) => void
}) => {
  const { t } = useTranslation()

  return (
    <Flex align="center" justify="space-between" gap="16px" flexWrap="wrap">
      {/* Left: read/edit actions */}
      <Flex align="center" gap="10px">
        {editMode ? (
          <>
            <Button
              colorPalette="blue"
              size="sm"
              onClick={onSave}
              loading={isSaving}
              disabled={editCount === 0}
              data-testid="user-list-save"
            >
              <Icon as={LuCheck} boxSize="15px" />
              {t('hatsuly.userList.save')}
              {editCount > 0 && (
                <Badge bg="whiteAlpha.300" color="white" borderRadius="full" size="sm">
                  {editCount}
                </Badge>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={onCancel} disabled={isSaving} data-testid="user-list-cancel">
              <Icon as={LuX} boxSize="15px" />
              {t('ui.cancel')}
            </Button>
          </>
        ) : (
          <>
            <Button colorPalette="blue" size="sm" onClick={onEnterEdit} data-testid="user-list-edit">
              <Icon as={LuPencil} boxSize="15px" />
              {t('hatsuly.userList.editHubspotId')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onRefetch}
              loading={isFetching}
              data-testid="user-list-refetch"
            >
              <Icon as={LuRefreshCw} boxSize="15px" />
              {t('hatsuly.userList.refetch')}
            </Button>
          </>
        )}
      </Flex>

      {/* Right: search */}
      <Box w="360px" maxW="60vw">
        <InputGroup
          startElement={<Icon as={LuSearch} boxSize="16px" color="gray.400" />}
          endElement={
            search ? (
              <IconButton
                aria-label={t('hatsuly.userList.clearSearch')}
                size="xs"
                variant="ghost"
                onClick={() => onSearchChange('')}
              >
                <Icon as={LuX} boxSize="14px" />
              </IconButton>
            ) : undefined
          }
        >
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('hatsuly.userList.searchPlaceholder')}
            size="sm"
            bg="white"
            data-testid="user-list-search"
          />
        </InputGroup>
      </Box>
    </Flex>
  )
}

export default UserListToolbar
